import React from 'react';
import { ArrowRight, Code2, Blocks, Sparkles } from 'lucide-react';
import Button from '@/components/ui/Button';
import { motion } from 'framer-motion';

const HeroSection = () => {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orcsys-50 via-white to-orcsys-50" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,transparent)]" />
      
      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Top right decorative element */}
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute top-20 right-[5%] w-64 h-64"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orcsys-200 to-orcsys-300 rounded-full opacity-20 blur-3xl" />
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse"
            }}
            className="absolute inset-0 bg-gradient-to-br from-orcsys-200 to-orcsys-300 rounded-full opacity-20 blur-2xl"
          />
        </motion.div>

        {/* Bottom left decorative element */}
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="absolute bottom-20 left-[5%] w-48 h-48"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-200 to-orcsys-300 rounded-full opacity-20 blur-3xl" />
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, -5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: "reverse"
            }}
            className="absolute inset-0 bg-gradient-to-tr from-orcsys-200 to-orcsys-300 rounded-full opacity-20 blur-2xl"
          />
        </motion.div>

        {/* Floating icons */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="absolute top-1/4 right-[15%]"
        >
          <motion.div
            animate={{
              y: [0, -10, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Code2 className="w-12 h-12 text-orcsys-500 opacity-50" />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="absolute bottom-1/3 left-[20%]"
        >
          <motion.div
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Blocks className="w-10 h-10 text-orcsys-400 opacity-50" />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="absolute top-1/3 left-[25%]"
        >
          <motion.div
            animate={{
              y: [0, -15, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Sparkles className="w-8 h-8 text-orcsys-300 opacity-50" />
          </motion.div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto text-center"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Développement Web pour Startups
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Transformez votre vision en réalité avec des solutions web performantes et évolutives, 
            conçues pour accélérer votre croissance.
          </p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button.Link href="#book" size="lg">
              Démarrer votre projet
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button.Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection; 